/**
 * GA4/GTM dataLayer tracking utilities.
 *
 * All custom events are pushed to `window.dataLayer` so that
 * Google Tag Manager can forward them to GA4.
 *
 * GTM container: GTM-T8K89CWW
 * GA4 measurement ID: G-HTJ20G0K5M
 */

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

function pushToDataLayer(data: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(data);
}

/** Enrich SPA route change with page metadata for GA4 pageviews. */
export function trackPageView(): void {
  pushToDataLayer({
    event: 'gatsby-route-change',
    page_location: window.location.href,
    page_path: window.location.pathname,
    page_title: document.title,
  });
}

/**
 * CTA click to start creating an invitation.
 *
 * Fired from:
 *  - "Opret ny invitation" button on /min-konto/mine-invitationer/
 *  - "Vælg dette design" confirmation on /opret-invitation/
 */
export function trackCTAOpretInvitation(
  ctaText: string,
  ctaLocation: string,
  userLoggedIn?: boolean,
): void {
  pushToDataLayer({
    event: 'CTA_opret_invitation',
    cta_text: ctaText,
    cta_location: ctaLocation,
    user_logged_in: userLoggedIn,
    page_location: window.location.href,
    page_path: window.location.pathname,
  });
}

/**
 * User clicked "Vælg dette design" — intent to use a design.
 * Fires regardless of login status.
 */
export function trackDesignSelectIntent(
  designName: string,
  designId: number,
  designCategory?: string,
): void {
  pushToDataLayer({
    event: 'design_select_intent',
    design_name: designName,
    design_id: designId,
    design_category: designCategory || undefined,
    page_location: window.location.href,
    page_path: window.location.pathname,
  });
}

/**
 * Design selection confirmed — project transitioned from draft to publish.
 * Matches the internal `usage_count` metric exactly.
 */
export function trackDesignSelectConfirmed(
  designName: string,
  designId: number,
): void {
  pushToDataLayer({
    event: 'design_select_confirmed',
    design_name: designName,
    design_id: designId,
    page_location: window.location.href,
    page_path: window.location.pathname,
  });
}

export interface PurchaseItem {
  item_id: string;
  item_name: string;
  quantity: number;
  price: number;
}

export interface PurchaseParams {
  transactionId: string;
  value: number;
  currency?: string;
  tax?: number;
  shipping?: number;
  coupon?: string;
  items: PurchaseItem[];
}

/** GA4 standard ecommerce purchase event. */
export function trackPurchase(params: PurchaseParams): void {
  pushToDataLayer({
    event: 'purchase',
    transaction_id: params.transactionId,
    value: params.value,
    currency: params.currency || 'DKK',
    tax: params.tax ?? 0,
    shipping: params.shipping ?? 0,
    coupon: params.coupon || '',
    items: params.items,
  });
}
